/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Dashboard
 *| (C) Copyright IBM Corp. 2017, 2020
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */

define([
	'../../../../lib/@waca/core-client/js/core-client/ui/core/Class',
	'../../../../util/TransactionUtil',
	'../../../../util/ColorUtils',
	'../../../../features/content/dataQueryExecution/DataQueryUtils',
	'underscore'
], function (
	Class,
	TransactionUtil,
	ColorUtils,
	DataQueryUtils,
	_
) {
	'use strict';

	/**
	 * @classdesc provides public APIs to access conditional palettes
	 */
	var VisConditionsSupport = Class.extend({

		// Slot ID of the heat slot
		HEAT_SLOT: 'heat',

		/**
		 * @constructs
		 * @public
		 * @param {Object}  params - Mandatory: parameters to initialize the VisConditionsManager.
		 * @param {Object}  params.visModel - Mandatory: the vis model
		 */
		init: function (params) {
			VisConditionsSupport.inherited('init', this, arguments);
			this.visModelManager = params.visModelManager;
			this.widgetModel = params.widgetModel;
			this.pageContext = params.pageContext;
			this.dashboardApi = params.dashboardApi;
			this.visualization = params.visualization;
			this.dataModel = params.dataModel;
			this.colorsService = this.dashboardApi.getFeature('Colors');
		},

		// expose as deprecate feature for now
		getAPI: function() {
			return this;
		},

		initialize: function (visAPI) {
			this.visAPI = visAPI;
			if (!this._isNewConditionalFormatFeature()) {
				this.widgetModel.on('change:conditions', this._onConditionsChanged, this);
				this.visModelManager.content.on('change:property:condColorPalette', this._onConditionsPropertiesChanged, this);
				this.visModelManager.content.on('change:property:condColorOrder', this._onConditionsPropertiesChanged, this);

				return this._initHeatPalette();
			}
		},

		destroy () {
			if (!this._isNewConditionalFormatFeature()) {
				this.widgetModel.off('change:conditions', this._onConditionsChanged, this);
				this.visModelManager.content.off('change:property:condColorPalette', this._onConditionsPropertiesChanged, this);
				this.visModelManager.content.off('change:property:condColorOrder', this._onConditionsPropertiesChanged, this);
			}
		},

		_initHeatPalette: function () {
			const paletteName = this.visAPI.getPropertyValue('condColorPalette');
			return this.colorsService.getPalette({
				paletteId: paletteName,
				type: 'ConditionalPalette',
				defaultIfNotFound: true
			}).then((colorPalette) => {
				this._heatPalette = _.clone(colorPalette.fills);
				this._heatPalette = this.visAPI.getPropertyValue('condColorOrder') === 'DarkerForLowerValue' ? this._heatPalette.reverse() : this._heatPalette;

				// Augment the conditions model with the colors needed at init time since we no longer save the colors in the spec
				const palette = this._getConditionalPalette();
				if (palette) {
					this._applyConditionsToPalette();
				}

				return this._heatPalette;
			});
		},

		_getConditionalPalette: function() {
			const conditions = this.visModelManager.getConditions(this.hasHeatByItem());
			return conditions ? conditions.palette : null;
		},

		/**
		 * Triggered when the owner widget's conditions model change.
		 * Updates the palette values with the new ones from the event.
		 * Notifies the palette picker and viz views about these changes.
		 *
		 * @param event Event object
		 */
		_onConditionsChanged: function(event = {}) {
			let promise;
			const names = ['change:conditions', 'change:visTransaction', 'change:visId'];
			if (event.sender === 'UndoRedoController' && names.includes(event.eventName)) {
				// Remap the conditional palette with the new colors
				const palette = this._getConditionalPalette();
				if (palette) {
					palette.sortConditionalPaletteByValue();
					promise = this.updateConditionalPalette().then(() => {
						palette.trigger('palette:refreshSlider');
					});
				}
			}
			return promise ? promise : Promise.resolve();
		},

		/**
		 * Triggered when the owner widget's condColorOrder or condColorPalette change
		 * @param event Event object
		 */
		_onConditionsPropertiesChanged: function(event) {
			const palette = this._getConditionalPalette();
			if(!event || !palette) {
				return Promise.resolve();
			}
			return this.updateConditionalPalette().then(() => {
				palette.trigger('palette:refreshSlider');
			});
		},

		/**
		 * Determines whether the heat slot has been mapped
		 *
		 * @return true if the heat slot is mapped, otherwise false
		 */
		hasHeatByItem: function() {
			var oHeatSlot = this.visualization.getSlots().getSlot(this.HEAT_SLOT);
			return oHeatSlot && oHeatSlot.getDataItemList().length > 0;
		},

		/**
		 * Get new color palette.
		 * @param options - options.
		 */
		initConditionalPalette: function(options) {
			if (!this._isNewConditionalFormatFeature()) {
				options = options || {};

				let newOption = {};
				if (options.payloadData && options.payloadData.transactionToken){
					newOption = TransactionUtil.transactionTokenToOptions(options.payloadData.transactionToken);
				}

				if (this.hasHeatByItem() && options.sender !== `UndoRedoController`) {
					return this.getConditionalMinMaxValue().then(function(data) {
						return this.createDefaultConditionalPalette(data.min, data.max, newOption);
					}.bind(this));
				} else {
					return Promise.resolve();
				}
			}
			return Promise.resolve();
		},

		/**
		 * Obtain the min and max value of the conditional(heat) measure
		 *
		 * @return promise which resolves an object that contains the min and max value of the conditional measure
		 */
		getConditionalMinMaxValue: function() {
			const slots = this.visualization.getSlots();
			let heatBySlot = slots.getSlot(this.HEAT_SLOT);
			let heatByDataItemUId = heatBySlot.getDataItemList()[0].getId();
			let dataItemList = [];
			_.each(slots.getSlotList(), (slot) => {
				_.each(slot.getDataItemList(), (dataItem) => {
					const supported = !dataItem.getBinning() && dataItem.getId() !== '_multiMeasuresSeries';
					if (!supported) {
						return;
					}

					const slotType = slot.getDefinition().getType();
					// Only add attribute DataItem and the Heating Data Item to the DataItemList
					const subType = slot.getDefinition().getSubType();
					const dataItemType = (subType === 'latitude' || subType === 'longitude') ? 'attribute' : dataItem.getType();
					if(slotType === 'category' || (slotType === 'any' && dataItemType === 'attribute') || dataItem.getId() === heatByDataItemUId){
						dataItemList.push(dataItem);
					}
				});
			});

			const content = this.dashboardApi.getCanvas().getContent(this.widgetModel.getId());
			const dataSource = this.visualization.getDataSource();
			let filterSpecList = DataQueryUtils.getFilterSpecListByVisualization(content, this.dashboardApi);
			filterSpecList.push(...this.visualization.getLocalFilters().getFilterList());
			const dataItemColumnIds = _.uniq(dataItemList.map(dataItem => dataItem.getColumnId()));
			filterSpecList = _.filter(filterSpecList, filter => {
				const filterColumnProjected = dataItemColumnIds.indexOf(filter.columnId) !== -1;
				if (filterColumnProjected) {
					return filterColumnProjected;
				}
				const filterColumn = dataSource.getMetadataColumn(filter.columnId);
				return filterColumn && filterColumn.getType() === 'attribute';
			});
			const queryService = this.dashboardApi.getFeature('QueryService');
			return queryService.getDataItemsMinMaxValues(
				dataSource.getId(),
				dataItemList,
				[heatByDataItemUId],
				filterSpecList
			).then(queryResult => {
				const res = { min: undefined, max: undefined };
				if (queryResult.getRowCount()) {
					res.min = Math.floor(queryResult.getValue(0, 0).value);
					res.max = Math.ceil(queryResult.getValue(0, 1).value);
				}
				return res;
			});
		},

		/**
		 * Create the default conditional palette with the given min/max value
		 *
		 * @param min	Minimum value of the conditional measure
		 * @param max	Maximum value of the conditional measure
		 * @param options	Options object
		 */
		createDefaultConditionalPalette: function (min, max, options) {
			var paletteName = this.visAPI.getPropertyValue('condColorPalette');
			var order = this.visAPI.getPropertyValue('condColorOrder');

			if (this.min == min && this.max == max){
				return Promise.resolve();
			}

			this.min = min;
			this.max = max;

			return this.colorsService.getPalette({
				paletteId: paletteName,
				type: 'ConditionalPalette'
			}).then((conditionalPalette) => {
				let heatPalette = _.clone(conditionalPalette.fills);
				heatPalette = order === 'DarkerForLowerValue' ? heatPalette.reverse() : heatPalette;

				const palette = this._getConditionalPalette();
				if (palette) {
					// setup a default conditional palette
					const indexMap = this._buildIndexMap(heatPalette, 5 /* max default number of colors is 5*/);
					const size = indexMap.length;
					const step = (max - min) / size;
					const defaultColors = [];
					for (var i = 0; i < size; i++) {
						const paletteColorIndex = indexMap[i];
						let colorObj = {
							value: min + (i * step)
						};
						if (heatPalette[paletteColorIndex].pattern) {
							colorObj.color = heatPalette[paletteColorIndex].color;
							colorObj.bgcolor = heatPalette[paletteColorIndex].fill;
							colorObj.pattern = heatPalette[paletteColorIndex].pattern;
						} else {
							colorObj.bgcolor = heatPalette[paletteColorIndex];
							colorObj.color = ColorUtils.invertColor(colorObj.bgcolor);
						}
						defaultColors.push(colorObj);
					}
					defaultColors.push({
						value: max,
						bgcolor: ''
					});
					palette.clear(options);
					palette.addColors(defaultColors, options);
				}

				return palette;
			});
		},

		/**
		 * update the conditional palette with new color palette and order
		 */
		updateConditionalPalette: function () {
			return this.hasHeatByItem() ? this._initHeatPalette() : Promise.resolve();
		},

		/**
		 * apply the given color palette and order to the conditional palette
		 */
		_applyConditionsToPalette: function () {
			const palette = this._getConditionalPalette();
			if (palette) {
				let szPalette = palette.getSize();
				if (szPalette > 0) {
					const indexMaps = this._buildIndexMap(this._heatPalette, szPalette-1);

					// If the new palette has fewer colors then the number of handles we currently have, don't both updating the
					// slider, we'll refresh the entire thing when we re-render the widget.
					let updateSlider = true;

					if (szPalette > (indexMaps.length + 1)) {
						updateSlider = false;
						//let midPoint = Math.floor(indexMaps.length / 2);
						const removeCount = szPalette - indexMaps.length - 1;
						palette.removeStyles(1, removeCount, {
							silent: true,
							updateSlider: updateSlider
						});
						szPalette = palette.getSize();
					}

					// map the conditional palette with the new colors
					for (var i = 0; i < indexMaps.length; i++) {
						var color = palette.getColor(i);
						var condColor = this._heatPalette[indexMaps[i]];
						if (condColor.pattern) {
							color.setPattern(condColor.pattern);
							color.setColor(condColor.color);
							color.setBackgroundColor(condColor.fill);
						} else {
							color.setBackgroundColor(condColor);
							color.setPattern(null);
							color.setColor(null);
						}
					}

					palette.updateColors(null, {
						silent: true,
						updateSlider: updateSlider
					});
				}
			}
		},

		_buildIndexMap: (palette, sampleSize) => {
			if (!palette || !sampleSize) {
				return [];
			}

			// If we're being asked for a bigger sample size then we have colors just return a map for the size of the palette
			if (palette.length <= sampleSize) {
				return _.range(palette.length);
			}

			// For backwards compatibility keep the same mapping for anything with 5 colors and less.
			if (palette.length === 5) {
				const fiveColorPalleteMaps = [
					[4], // one color
					[0, 4], // two colors
					[0, 2, 4], // three colors
					[0, 1, 2, 4], // four colors
					[0, 1, 2, 3, 4] // five colors
				];
				return fiveColorPalleteMaps[sampleSize-1];
			}

			// Only asking for one color, grab the middle one
			if (sampleSize === 1) {
				return [Math.floor(palette.length / 2)];
			}

			const step = (palette.length - 1) / sampleSize;
			const indexMap = [0];
			for (let i = 2; i < sampleSize; i++) {
				indexMap.push(Math.floor(step * i));
			}
			indexMap.push(palette.length - 1);

			return indexMap;
		},

		getConditionalPaletteLength: function() {
			return this._heatPalette.length;
		},

		_isNewConditionalFormatFeature () {
			return !this.dashboardApi.getGlassCoreSvc('.FeatureChecker').checkValue('dashboard', 'xtabcondFormat', 'disabled');
		}

	});

	return VisConditionsSupport;
});
